import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import TranslateIcon from '@mui/icons-material/Translate';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import { apiClient } from '../services/apiClient';

const PROMPT_SEPARATOR = '#####';

const parseText = (text) => {
  return (text || '')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\r/g, '\r');
};

const convertTextForSaving = (text) => {
  return text
    .replace(/\n/g, '\\n')
    .replace(/\t/g, '\\t')
    .replace(/\r/g, '\\r');
};

const splitPromptValue = (value) => {
  const parsed = parseText(value);
  const separatorIndex = parsed.indexOf(PROMPT_SEPARATOR);

  if (separatorIndex === -1) {
    return { systemPrompt: parsed, userPrompt: '' };
  }

  return {
    systemPrompt: parsed.slice(0, separatorIndex),
    userPrompt: parsed.slice(separatorIndex + PROMPT_SEPARATOR.length),
  };
};

const joinPromptValue = (systemPrompt, userPrompt) => {
  return `${systemPrompt}${PROMPT_SEPARATOR}${userPrompt}`;
};

const containsSeparator = (text) => (text || '').includes(PROMPT_SEPARATOR);

export default function Prompts() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBackupAllDialog, setOpenBackupAllDialog] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [formData, setFormData] = useState({
    role: '',
    type: '',
    langue: '',
    systemPrompt: '',
    userPrompt: '',
  });
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    role: '',
    type: '',
    langue: 'fr',
    value: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedDetailPrompt, setSelectedDetailPrompt] = useState(null);
  const [openTranslateDialog, setOpenTranslateDialog] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState({ current: 0, total: 0 });
  const SUPPORTED_LANGUAGES = ['fr', 'en', 'de', 'it'];

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const data = await apiClient.get('/api/admin/prompts');
      setPrompts(data);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setFormError('Impossible de charger les prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (prompt = null) => {
    if (prompt) {
      const { systemPrompt, userPrompt } = splitPromptValue(prompt.value);
      setSelectedPrompt(prompt);
      setFormData({
        role: prompt.role,
        type: prompt.type,
        langue: prompt.langue,
        systemPrompt,
        userPrompt,
      });
    } else {
      setSelectedPrompt(null);
      setFormData({
        role: '',
        type: '',
        langue: '',
        systemPrompt: '',
        userPrompt: '',
      });
    }
    setOpenDialog(true);
    setFormError(null);
    setFormSuccess(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPrompt(null);
    setFormData({
      role: '',
      type: '',
      langue: '',
      systemPrompt: '',
      userPrompt: '',
    });
    setFormError(null);
    setFormSuccess(false);
  };

  const handleOpenDeleteDialog = (prompt) => {
    setSelectedPrompt(prompt);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedPrompt(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (containsSeparator(formData.systemPrompt) || containsSeparator(formData.userPrompt)) {
      setFormError(`La chaîne "${PROMPT_SEPARATOR}" est interdite dans les champs de prompt. Merci de la supprimer afin de pouvoir enregistrer le prompt.`);
      return;
    }

    try {
      setSaving(true);
      setFormError(null);
      setFormSuccess(false);

      const combinedValue = joinPromptValue(formData.systemPrompt, formData.userPrompt);
      const dataToSend = {
        role: formData.role.trim(),
        type: formData.type.trim(),
        langue: formData.langue.trim(),
        value: convertTextForSaving(combinedValue),
      };

      if (selectedPrompt) {
        await apiClient.put(`/api/admin/prompts/${selectedPrompt.id}`, dataToSend);
      } else {
        await apiClient.post('/api/admin/prompts', dataToSend);
      }

      await fetchPrompts();
      handleCloseDialog();
      showSnackbar(selectedPrompt ? 'Prompt mis à jour avec succès' : 'Prompt créé avec succès');
    } catch (error) {
      console.error('Error saving prompt:', error);
      setFormError('Impossible d\'enregistrer le prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      setFormError(null);

      await apiClient.delete(`/api/admin/prompts/${selectedPrompt.id}`);

      await fetchPrompts();
      handleCloseDeleteDialog();
      showSnackbar('Prompt supprimé avec succès');
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setFormError('Impossible de supprimer le prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToBackup = async (prompt) => {
    try {
      await apiClient.post(`/api/admin/prompts/${prompt.id}/backup`, {
        value: prompt.value
      });
      showSnackbar('Prompt sauvegardé avec succès');
    } catch (error) {
      console.error('Error backing up prompt:', error);
      showSnackbar('Impossible de sauvegarder le prompt', 'error');
    }
  };

  const handleRestoreBackup = async (prompt) => {
    try {
      await apiClient.post(`/api/admin/prompts/${prompt.id}/restore`, {});
      await fetchPrompts();
      showSnackbar('Prompt restauré avec succès');
    } catch (error) {
      console.error('Error restoring prompt:', error);
      showSnackbar('Impossible de restaurer le prompt', 'error');
    }
  };

  const handleOpenDetailDialog = (prompt) => {
    setSelectedDetailPrompt(prompt);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedDetailPrompt(null);
  };

  const filteredPrompts = prompts.filter(prompt => {
    return (
      prompt.role.toLowerCase().includes(filters.role.toLowerCase()) &&
      prompt.type.toLowerCase().includes(filters.type.toLowerCase()) &&
      prompt.langue.toLowerCase().includes(filters.langue.toLowerCase()) &&
      prompt.value.toLowerCase().includes(filters.value.toLowerCase())
    );
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenBackupAllDialog = () => {
    setOpenBackupAllDialog(true);
  };

  const handleCloseBackupAllDialog = () => {
    setOpenBackupAllDialog(false);
  };

  const handleBackupAll = async () => {
    try {
      setSaving(true);
      setFormError(null);

      await apiClient.post('/api/admin/prompts/backup-all', {});

      handleCloseBackupAllDialog();
      showSnackbar('Tous les prompts ont été sauvegardés avec succès');
    } catch (error) {
      console.error('Error backing up all prompts:', error);
      setFormError('Impossible de sauvegarder tous les prompts');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenTranslateDialog = (prompt) => {
    setSelectedPrompt(prompt);
    setOpenTranslateDialog(true);
  };

  const handleCloseTranslateDialog = () => {
    setOpenTranslateDialog(false);
    setSelectedPrompt(null);
    setTranslationProgress({ current: 0, total: 0 });
  };

  const handleTranslate = async () => {
    try {
      setTranslating(true);
      setFormError(null);

      const targetLanguages = SUPPORTED_LANGUAGES.filter(lang => lang !== selectedPrompt.langue);
      
      await apiClient.post(`/api/admin/prompts/${selectedPrompt.id}/translate`, {
        sourceLanguage: selectedPrompt.langue,
        targetLanguages: targetLanguages
      });
      
      await fetchPrompts();
      handleCloseTranslateDialog();
      showSnackbar('Prompt traduit avec succès');
    } catch (error) {
      console.error('Error translating prompt:', error);
      setFormError('Impossible de traduire le prompt');
    } finally {
      setTranslating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">
            Gestion des prompts
          </h2>
          <div className="flex gap-4">
            <button
              onClick={handleOpenBackupAllDialog}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <SaveIcon className="w-5 h-5 mr-2" />
              Backup de tous les prompts
            </button>
            <button
              onClick={() => handleOpenDialog()}
              className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
            >
              <AddIcon className="w-5 h-5 mr-2" />
              Créer un prompt
            </button>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Filtres</h3>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label htmlFor="role-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Rôle
              </label>
              <input
                type="text"
                id="role-filter"
                name="role"
                value={filters.role}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Filtrer par rôle"
              />
            </div>
            <div>
              <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <input
                type="text"
                id="type-filter"
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Filtrer par type"
              />
            </div>
            <div>
              <label htmlFor="langue-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Langue
              </label>
              <input
                type="text"
                id="langue-filter"
                name="langue"
                value={filters.langue}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Filtrer par langue"
              />
            </div>
            <div>
              <label htmlFor="value-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Contenu
              </label>
              <input
                type="text"
                id="value-filter"
                name="value"
                value={filters.value}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Filtrer par contenu"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Langue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contenu
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPrompts.map((prompt) => (
                <tr key={prompt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {prompt.role}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {prompt.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {prompt.langue}
                  </td>
                  <td 
                    className="px-6 py-4 text-sm text-gray-900 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleOpenDetailDialog(prompt)}
                  >
                    <div 
                      className="line-clamp-3 whitespace-pre-line"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: '3',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {parseText(prompt.value)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenDialog(prompt)}
                      className="text-blue-600 hover:text-blue-900 mr-2"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleCopyToBackup(prompt)}
                      className="text-green-600 hover:text-green-900 mr-2"
                    >
                      <SaveIcon className="w-4 h-4" />
                    </button>
                    <Tooltip 
                      title={prompt.backup ? `Valeur précédemment sauvegardée : ${prompt.backup}\nDate de sauvegarde : ${new Date(prompt.backup_at).toLocaleString()}` : "Pas de sauvegarde précédente"} 
                      placement="top"
                    >
                      <span>
                        <button
                          onClick={() => handleRestoreBackup(prompt)}
                          className="text-orange-600 hover:text-orange-900 mr-2"
                          disabled={!prompt.backup}
                        >
                          <RestoreIcon className="w-4 h-4" />
                        </button>
                      </span>
                    </Tooltip>
                    <button
                      onClick={() => handleOpenTranslateDialog(prompt)}
                      className="text-purple-600 hover:text-purple-900 mr-2"
                    >
                      <TranslateIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteDialog(prompt)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <DeleteIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Dialog */}
        {openDialog && (
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]"
            onClick={handleCloseDialog}
          >
            <div
              className="bg-white rounded-lg max-w-4xl w-full h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 shrink-0">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {selectedPrompt ? 'Modifier le prompt' : 'Créer un prompt'}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                      Rôle
                    </label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <input
                      type="text"
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="langue" className="block text-sm font-medium text-gray-700 mb-1">
                      Langue
                    </label>
                    <input
                      type="text"
                      id="langue"
                      name="langue"
                      value={formData.langue}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="px-6 py-4 flex-1 min-h-0 flex flex-col gap-3 overflow-hidden">
                  <div className="flex-[2] min-h-0 flex flex-col">
                    <label htmlFor="systemPrompt" className="block text-sm font-medium text-gray-700 mb-1 shrink-0">
                      Prompt système
                    </label>
                    <textarea
                      id="systemPrompt"
                      name="systemPrompt"
                      value={formData.systemPrompt}
                      onChange={handleInputChange}
                      className="flex-1 min-h-0 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono resize-none"
                      required
                    />
                  </div>

                  <div className="flex-[1] min-h-0 flex flex-col">
                    <label htmlFor="userPrompt" className="block text-sm font-medium text-gray-700 mb-1 shrink-0">
                      Prompt utilisateur
                    </label>
                    <textarea
                      id="userPrompt"
                      name="userPrompt"
                      value={formData.userPrompt}
                      onChange={handleInputChange}
                      className="flex-1 min-h-0 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono resize-none"
                    />
                  </div>

                  {formError && (
                    <div className="shrink-0 p-4 rounded-md bg-red-50 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  {formSuccess && (
                    <div className="shrink-0 p-4 rounded-md bg-green-50 text-sm text-green-700">
                      Opération réussie
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg shrink-0">
                  <button
                    type="button"
                    onClick={handleCloseDialog}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                      ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                      focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                  >
                    {saving ? 'Enregistrement...' : selectedPrompt ? 'Enregistrer' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {openDeleteDialog && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Supprimer le prompt
                </h3>
              </div>

              <div className="px-6 py-4">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir supprimer ce prompt ?
                </p>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseDeleteDialog}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving}
                  className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {saving ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail View Dialog */}
        {openDetailDialog && selectedDetailPrompt && (() => {
          const { systemPrompt, userPrompt } = splitPromptValue(selectedDetailPrompt.value);

          return (
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]"
              onClick={handleCloseDetailDialog}
            >
              <div
                className="bg-white rounded-lg max-w-4xl w-full h-[85vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-4 border-b border-gray-200 shrink-0">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Détail du prompt
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span><strong>Rôle:</strong> {selectedDetailPrompt.role}</span>
                    <span><strong>Type:</strong> {selectedDetailPrompt.type}</span>
                    <span><strong>Langue:</strong> {selectedDetailPrompt.langue}</span>
                  </div>
                </div>

                <div className="px-6 py-4 flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
                  <div className="flex-[2] min-h-0 flex flex-col rounded-lg border border-blue-200 bg-blue-50/40 overflow-hidden">
                    <div className="px-4 py-2 border-b border-blue-200 bg-blue-100/60 shrink-0">
                      <h4 className="text-sm font-semibold text-blue-900">Prompt système</h4>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
                      <div className="whitespace-pre-line text-sm text-gray-900 font-mono">
                        {systemPrompt || <span className="text-gray-400 italic">Aucun contenu</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex-[1] min-h-0 flex flex-col rounded-lg border border-amber-200 bg-amber-50/40 overflow-hidden">
                    <div className="px-4 py-2 border-b border-amber-200 bg-amber-100/60 shrink-0">
                      <h4 className="text-sm font-semibold text-amber-900">Prompt utilisateur</h4>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
                      <div className="whitespace-pre-line text-sm text-gray-900 font-mono">
                        {userPrompt || <span className="text-gray-400 italic">Aucun contenu</span>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end shrink-0">
                  <button
                    type="button"
                    onClick={handleCloseDetailDialog}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Backup All Confirmation Dialog */}
        {openBackupAllDialog && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Confirmer le backup
                </h3>
              </div>

              <div className="px-6 py-4">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir sauvegarder tous les prompts ? Cette action va créer une copie de sauvegarde de chaque prompt.
                </p>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseBackupAllDialog}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleBackupAll}
                  disabled={loading}
                  className="inline-flex justify-center rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} className="mr-2 text-white" />
                      Sauvegarde en cours...
                    </>
                  ) : (
                    'Confirmer le backup'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Translation Confirmation Dialog */}
        {openTranslateDialog && selectedPrompt && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Traduire le prompt
                </h3>
              </div>

              <div className="px-6 py-4">
                <p className="text-sm text-gray-500 mb-4">
                  Cette action va traduire le prompt depuis {selectedPrompt.langue} vers les autres langues supportées (fr, en, de, it).
                  {SUPPORTED_LANGUAGES.filter(lang => lang !== selectedPrompt.langue).map(lang => (
                    <span key={lang} className="block mt-2">
                      • {selectedPrompt.langue} → {lang}
                    </span>
                  ))}
                </p>
                {translating && (
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-purple-600 h-2.5 rounded-full transition-all duration-300" 
                        style={{ width: `${(translationProgress.current / translationProgress.total) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2 text-center">
                      Traduction en cours... ({translationProgress.current}/{translationProgress.total})
                    </p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseTranslateDialog}
                  disabled={translating}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleTranslate}
                  disabled={translating}
                  className="inline-flex justify-center rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {translating ? (
                    <>
                      <CircularProgress size={20} className="mr-2 text-white" />
                      Traduction en cours...
                    </>
                  ) : (
                    'Lancer la traduction'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
} 